import { useQuery } from "@apollo/client";
import _ from "lodash";
import { useContext } from "react";
import { useMatch } from "react-router";

import { GET_USER_ORGANIZATIONS } from "./queries";

import { authContext } from "context/auth";
import type { GetUserOrganizationsQuery } from "gql/graphql";
import { organizationStorage } from "storage/organization";
import { Logger } from "utils/logger";

interface IOrgStorage {
  deleted?: boolean;
  name?: string;
}

const hasValue = (value: string | undefined): boolean => {
  return value !== undefined && value !== "";
};

const getOrgFromUrl = (
  orgFromUrl: string | undefined,
  organizations: string[],
): string | undefined => {
  if (orgFromUrl === undefined || !organizations.includes(orgFromUrl)) {
    return undefined;
  }

  const orgObject: IOrgStorage = {
    deleted: false,
    name: orgFromUrl.toLowerCase(),
  };
  void organizationStorage.setItem("V2", JSON.stringify(orgObject));

  return orgFromUrl;
};

const getOrgFromLocalStorage = (orgs: string[]): string | undefined => {
  const lastOrganization = organizationStorage.getItem("V2");

  if (_.isNull(lastOrganization)) {
    return undefined;
  }

  const { name = "", deleted = false }: IOrgStorage =
    typeof lastOrganization === "string"
      ? JSON.parse(lastOrganization)
      : lastOrganization;

  if (name && !deleted && orgs.includes(name.toLowerCase())) {
    return name.toLowerCase();
  }

  return undefined;
};

const getOrgByDefault = (organizations: string[]): string | undefined => {
  const orgObject: IOrgStorage = { deleted: false, name: organizations[0] };
  void organizationStorage.setItem("V2", JSON.stringify(orgObject));

  return organizations[0];
};

const resolveOrganization = (
  orgFromUrl: string | undefined,
  organizations: { name: string }[] | undefined,
): string => {
  if (!organizations || organizations.length === 0) return "";

  const validOrganizations = organizations.map((organization): string =>
    organization.name.toLowerCase(),
  );

  return (
    getOrgFromUrl(orgFromUrl, validOrganizations) ??
    getOrgFromLocalStorage(validOrganizations) ??
    getOrgByDefault(validOrganizations) ??
    ""
  );
};

const useOrgMatch = (): ReturnType<typeof useMatch> =>
  useMatch("/orgs/:organizationName/*");

const useOrgFromUrl = (): string | undefined => {
  const orgMatch = useOrgMatch();

  return orgMatch?.params.organizationName;
};

const shouldQueryOrganizations = (
  organizationName: string | undefined,
  orgFromUrl: string | undefined,
): boolean => {
  return !hasValue(organizationName) && !hasValue(orgFromUrl);
};

const useUserOrganizations = (
  onCompleted?: (data: GetUserOrganizationsQuery) => void,
  skip?: boolean,
): ReturnType<typeof useQuery<GetUserOrganizationsQuery>> => {
  const { userEmail } = useContext(authContext);

  const shouldSkip =
    skip === undefined ? !hasValue(userEmail) : skip || !hasValue(userEmail);

  return useQuery<GetUserOrganizationsQuery>(GET_USER_ORGANIZATIONS, {
    onCompleted,
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        Logger.error("Couldn't get user organizations", error.message);
      });
    },
    skip: shouldSkip,
  });
};

export {
  resolveOrganization,
  hasValue,
  useOrgFromUrl,
  shouldQueryOrganizations,
  useUserOrganizations,
};
